/**
 * Typeahead Component Patch
 * This script patches the Typeahead component's methods to prevent null reference errors
 * and handle GraphQL data fetching properly
 */
import React from 'react';

export default function applyTypeaheadFix() {
  console.log('Loading Typeahead Patch...');
  
  function waitForTypeahead() {
    if (window.ReactBootstrapTypeahead && window.ReactBootstrapTypeahead.TypeaheadMenu) {
      console.log('Found Typeahead component, applying patches...');
      patchTypeahead();
    } else {
      console.log('Waiting for Typeahead component...');
      // In module system, we might need a different way if it's not on window
      // But preserving original logic for now
      if (typeof window.ReactBootstrapTypeahead === 'undefined') {
          // Try to see if it's imported globally via script tag (if any)
          // But here we are likely running in a bundle where it is not yet on window.
          // Since we import things in index.js, we might need to rely on the fact that
          // we are running AFTER libraries are loaded if they are in vendor bundle?
          // Actually, if we use imports in React, window.ReactBootstrapTypeahead might NOT exist.
          // This patch assumes the global variable exists, which was true for the script tag injection.
          // We will try running this logic, but if packages are bundled, we might need to rely on
          // resolving the module. However, without modifying node_modules, patching the prototype
          // of the imported object is the way.
          
          // Since we can't easily access the module instance from here without importing it,
          // checking window is the legacy way.
          // If this fails (loops forever), we might need to import ReactBootstrapTypeahead here.
          
          setTimeout(waitForTypeahead, 100);
      } else {
          setTimeout(waitForTypeahead, 100);
      }
    }
  }
  
  function patchTypeahead() {
    try {
      const TypeaheadMenu = window.ReactBootstrapTypeahead.TypeaheadMenu;
      const AsyncTypeahead = window.ReactBootstrapTypeahead.AsyncTypeahead;
      
      // Store original methods
      const originalRenderMenuItem = TypeaheadMenu.prototype._renderMenuItem;
      const originalGetOptionLabel = window.ReactBootstrapTypeahead.utils.getOptionLabel;
      
      // Patch getOptionLabel
      window.ReactBootstrapTypeahead.utils.getOptionLabel = function(option, labelKey) {
        try {
          // Handle loading state
          if (option === null || option === undefined) {
            return '';
          }
          
          // Handle GraphQL response format
          if (option.__typename) {
            // If it's a GraphQL response, try to get the label from the appropriate field
            if (labelKey) {
              if (typeof labelKey === 'function') {
                return labelKey(option) || '';
              }
              return option[labelKey] || '';
            }
            // Try common field names in GraphQL responses
            return option.name || option.label || option.value || option.id || '';
          }
          
          // Handle regular options
          if (typeof option === 'string') return option;
          if (typeof option === 'number') return String(option);
          if (labelKey) {
            if (typeof labelKey === 'function') {
              return labelKey(option) || '';
            }
            return option[labelKey] || '';
          }
          return option.label || option.name || option.value || '';
        } catch (error) {
          console.warn('[TypeaheadPatch] Error getting option label:', error);
          return '';
        }
      };
      
      // Patch _renderMenuItem
      TypeaheadMenu.prototype._renderMenuItem = function(option, props, index) {
        try {
          // Handle loading state
          if (!option) {
            console.warn('[TypeaheadPatch] Attempted to render null menu item');
            return null;
          }
          
          // Handle GraphQL loading state
          if (option.loading) {
            return React.createElement('div', {
              className: 'rbt-menu-item',
              key: 'loading'
            }, 'Loading...');
          }
          
          const label = window.ReactBootstrapTypeahead.utils.getOptionLabel(option, props.labelKey);
          if (!label) {
            console.warn('[TypeaheadPatch] Could not get label for option:', option);
            return null;
          }
          
          return originalRenderMenuItem.call(this, option, props, index);
        } catch (error) {
          console.warn('[TypeaheadPatch] Error rendering menu item:', error);
          return null;
        }
      };
      
      // Patch AsyncTypeahead if it exists
      if (AsyncTypeahead) {
        const originalAsyncTypeahead = AsyncTypeahead.prototype.render;
        AsyncTypeahead.prototype.render = function() {
          try {
            // Add loading state to options if needed
            if (this.state.isLoading && (!this.state.options || !this.state.options.length)) {
              this.state.options = [{ loading: true }];
            }
            return originalAsyncTypeahead.apply(this, arguments);
          } catch (error) {
            console.warn('[TypeaheadPatch] Error in AsyncTypeahead render:', error);
            return null;
          }
        };
      }
      
      console.log('Typeahead component patched successfully');
      
      // Apply Payment Terms Patch Immediately
      patchPaymentTerms();
      
    } catch (error) {
      console.error('Error patching Typeahead component:', error);
    }
  }
  
  function patchPaymentTerms() {
     console.log('Initializing Typeahead patch for QuickBooks payment terms...');
     const { TypeaheadMenu, AsyncTypeahead } = window.ReactBootstrapTypeahead;
     
     // Store original implementations
     const originalTypeaheadMenu = TypeaheadMenu;
     const originalAsyncTypeahead = AsyncTypeahead;

     // Helper function to format QuickBooks term data
     function formatQuickBooksTerm(term) {
       if (!term) return null;
       
       // Handle different possible term formats
       const name = term.Name || term.name || term.label || '';
       const id = term.Id || term.id || term.value || '';
       
       return {
         id: id,
         label: name,
         value: id,
         name: name,
         // Include all original properties
         ...term
       };
     }

     // Helper function to ensure options are properly formatted
     function formatOptions(options) {
       if (!options) return [];
       if (!Array.isArray(options)) {
         options = [options];
       }
       return options.map(formatQuickBooksTerm).filter(Boolean);
     }

     // Patch TypeaheadMenu to handle QuickBooks format
     window.ReactBootstrapTypeahead.TypeaheadMenu = function(props) {
       try {
         // Format options if they're from QuickBooks
         const formattedOptions = formatOptions(props.options);
         
         // Add loading state handling
         if (props.isLoading) {
           return React.createElement('div', { className: 'rbt-menu' },
             React.createElement('div', { className: 'rbt-menu-item' },
               React.createElement('div', { className: 'text-center' },
                 React.createElement('span', { className: 'spinner-border spinner-border-sm' }),
                 ' Loading payment terms...'
               )
             )
           );
         }

         // If no options, show empty state
         if (!formattedOptions || formattedOptions.length === 0) {
           return React.createElement('div', { className: 'rbt-menu' },
             React.createElement('div', { className: 'rbt-menu-item' },
               'No payment terms found'
             )
           );
         }

         // Call original TypeaheadMenu with formatted options
         return originalTypeaheadMenu({
           ...props,
           options: formattedOptions
         });
       } catch (error) {
         console.error('Error in patched TypeaheadMenu:', error);
         return React.createElement('div', { className: 'rbt-menu' },
           React.createElement('div', { className: 'rbt-menu-item text-danger' },
             'Error loading payment terms'
           )
         );
       }
     };

     // Patch AsyncTypeahead to handle QuickBooks format
     if (originalAsyncTypeahead) {
       window.ReactBootstrapTypeahead.AsyncTypeahead = function(props) {
         try {
           // Format options if they're from QuickBooks
           const formattedOptions = formatOptions(props.options);
           
           // Add loading state handling
           const isLoading = props.isLoading || false;
           
           // Call original AsyncTypeahead with formatted options
           return originalAsyncTypeahead({
             ...props,
             options: formattedOptions,
             isLoading: isLoading
           });
         } catch (error) {
           console.error('Error in patched AsyncTypeahead:', error);
           return React.createElement('div', { className: 'rbt-input-wrapper' },
             React.createElement('input', {
               type: 'text',
               className: 'form-control',
               disabled: true,
               placeholder: 'Error loading payment terms'
             })
           );
         }
       };
     }

     console.log('Typeahead patch applied successfully');
  }

  // Start the process
  waitForTypeahead();
}



// WEBPACK FOOTER //
// ./src/utils/patches/typeaheadFix.js